import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, ArrowDown, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useConversation } from '../hooks/useConversation';
import TurnView from '../components/chat/TurnView';
import EnhancedChatInput from '../components/EnhancedChatInput';
import { MessageRequest } from '../schemas/chat';
import { MessageNavigator } from '../components/chat/MessageNavigator';
import { useActiveMessage } from '../hooks/useActiveMessage';
import { TurnId } from '../types/conversation';

const ChatPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state,
    conversation,
    turns,
    chatError,
    suggestedActions,
    suggestedActionsLoading,
    createTurn,
    selectedModel,
    selectModel,
    models,
    kbState,
    selectKnowledgebase,
    selectAllKnowledgebases,
    clearAllKnowledgebases,
    literatureSources,
    selectLiterature,
    literatureOptions,
    selectLiteratureOptions,
  } = useConversation();

  // ============================================================================
  // UI 状态
  // ============================================================================
  const [searchQuery, setSearchQuery] = useState('');
  const [turnLikes, setTurnLikes] = useState<Record<string, 'like' | 'dislike' | null>>({});
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);

  // Message refs for navigation - track user message elements
  // Use a ref to store the map of refs to avoid re-creation
  const userMessageRefsMapRef = useRef<Map<TurnId, HTMLDivElement | null>>(new Map());

  // Get or create a ref callback for a specific turn ID
  const getUserMessageRef = useCallback((turnId: TurnId) => {
    return (element: HTMLDivElement | null) => {
      if (element) {
        userMessageRefsMapRef.current.set(turnId, element);
      } else {
        userMessageRefsMapRef.current.delete(turnId);
      }
    };
  }, []);

  // Convert the map to the format expected by useActiveMessage
  // We use useState with a setter function that's called via useEffect when turns change
  const [userMessageRefsMap, setUserMessageRefsMap] = useState<
    Map<TurnId, React.RefObject<HTMLDivElement | null>>
  >(new Map());

  // 检查是否有正在流式传输的 Turn
  const isStreaming = turns.some(t => t.status === 'streaming');

  // ============================================================================
  // Turn 过滤 - moved here to be available for useEffect dependencies
  // ============================================================================
  const filteredTurns = useMemo(() => {
    if (state.type !== 'ready' || !conversation) return [];

    if (!searchQuery.trim()) return turns;

    const lowerSearchQuery = searchQuery.toLowerCase();
    return turns.filter(
      turn =>
        turn.content.text.toLowerCase().includes(lowerSearchQuery) ||
        turn.answerText.toLowerCase().includes(lowerSearchQuery)
    );
  }, [state.type, conversation, turns, searchQuery]);

  // Update userMessageRefsMap when turns or filtered turns change
  // This ensures refs are updated when filtering changes
  useEffect(() => {
    const map = new Map<TurnId, React.RefObject<HTMLDivElement | null>>();
    userMessageRefsMapRef.current.forEach((element, turnId) => {
      map.set(turnId, { current: element });
    });
    setUserMessageRefsMap(map);
  }, [turns, filteredTurns]);

  // 重置状态当流式传输开始或结束时
  useEffect(() => {
    if (!isStreaming) {
      setAutoScrollPaused(false);
      setShowScrollButton(false);
    }
  }, [isStreaming]);

  // ============================================================================
  // Refs - DOM 操作
  // ============================================================================
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Track active message for navigation
  const activeMessageId = useActiveMessage({
    containerRef: messagesContainerRef,
    messageRefs: userMessageRefsMap,
  });

  // ============================================================================
  // 工具函数
  // ============================================================================
  /**
   * Get model display name from modelRef
   * modelRef now stores the model_name directly from the API response (e.g., "DeepSeek-V3.1")
   * If modelRef looks like an ID (not a display name), try to find the model in the models list
   */
  const getModelDisplayName = (modelRef: string | undefined): string => {
    if (!modelRef) return 'AI';
    // If modelRef doesn't look like an ID (contains letters, numbers, dots, dashes, spaces),
    // it's likely already a display name
    if (/^[A-Za-z0-9.\-+\s]+$/.test(modelRef) && modelRef.length > 3) {
      return modelRef;
    }
    // Otherwise try to find it in the models list
    const model = models.find(m => m.id === modelRef);
    return model?.display_name || model?.name || modelRef;
  };

  /**
   * Get the latest AI message's modelRef from turns
   * Returns the modelRef of the last turn that has an answer
   */
  const getLatestModelRef = (): string | undefined => {
    // Find the last turn with an answer (completed or streaming)
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].answerText || turns[i].status === 'streaming') {
        return turns[i].modelRef;
      }
    }
    return undefined;
  };

  // ============================================================================
  // 滚动控制逻辑
  // ============================================================================
  const scrollToBottom = useCallback((smooth = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    });
  }, []);

  // 检查是否在底部附近
  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const { scrollTop, clientHeight, scrollHeight } = container;
    const threshold = 150; // 150px 阈值
    return scrollTop + clientHeight >= scrollHeight - threshold;
  }, []);

  // 流式传输时持续自动滚动（除非被用户暂停）
  useEffect(() => {
    if (!isStreaming || autoScrollPaused) return;

    const intervalId = setInterval(() => {
      scrollToBottom(false);
    }, 100);

    return () => clearInterval(intervalId);
  }, [isStreaming, autoScrollPaused, scrollToBottom]);

  // 监听用户手动滚动
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let lastScrollTop = container.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const isNearBottom = checkIfNearBottom();
      const isScrollingUp = currentScrollTop < lastScrollTop;

      // 检测用户是否向上滚动（不在底部）
      if (isStreaming && isScrollingUp && !isNearBottom && !autoScrollPaused) {
        setAutoScrollPaused(true);
        setShowScrollButton(true);
      }

      // 如果用户滚动回底部，重新启用自动滚动
      if (isNearBottom && autoScrollPaused) {
        setAutoScrollPaused(false);
        setShowScrollButton(false);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [checkIfNearBottom, isStreaming, autoScrollPaused]);

  // 新消息完成时滚动到底部
  useEffect(() => {
    if (state.type === 'ready' && turns.length > 0 && !isStreaming) {
      scrollToBottom(true);
    }
  }, [state.type, turns.length, isStreaming, scrollToBottom]);

  // ============================================================================
  // Turn 处理函数
  // ============================================================================
  const handleCreateTurn = async (data: MessageRequest) => {
    if (state.type !== 'ready') return;
    try {
      // Convert UploadedFile[] to FileMetadata[] for attachments
      const attachmentMetadata =
        data.files?.map(f => ({
          file_id: f.id,
          file_name: f.original_name || f.name || 'Unknown',
          file_type: f.file_type || 'document',
        })) || [];

      console.log('handleCreateTurn called with:', {
        content: data.content,
        filesCount: data.files?.length || 0,
        attachmentMetadata,
        files: data.files,
        literature_sources: data.literature_sources,
        literature_options: data.literature_options,
      });

      const result = await createTurn({
        content: {
          text: data.content,
          attachments: attachmentMetadata.length > 0 ? attachmentMetadata : undefined,
        },
        modelRef: selectedModel || undefined,
        setActive: true,
        literature_sources: data.literature_sources,
        literature_options: data.literature_options || undefined,
      });

      console.log('createTurn result:', result);
      console.log(`Created turn with ${attachmentMetadata.length} file attachments`);
    } catch (error) {
      console.error('Failed to create turn:', error);
    }
  };

  const handleEditTurn = async (turnId: string, content: string) => {
    // TODO: 实现编辑 Turn 的逻辑（创建新的 Turn 基于父 Turn）
    console.log('Edit turn:', turnId, content);
  };

  const handleCopyTurn = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const handleLikeTurn = (turnId: string, type: 'like' | 'dislike') => {
    setTurnLikes(prev => ({
      ...prev,
      [turnId]: prev[turnId] === type ? null : type,
    }));
  };

  const handleSuggestedAction = (action: string) => {
    handleCreateTurn({ content: action });
  };

  // Handle message navigator click - scroll to user message and highlight it
  const handleNavigatorMessageClick = useCallback(
    (messageId: TurnId, elementRef: React.RefObject<HTMLDivElement | null>) => {
      const element = elementRef.current;
      if (!element) return;

      // Find the user message element within the turn (data-testid="turn-user")
      const userMessageElement = element.querySelector('[data-testid="turn-user"]');
      if (userMessageElement) {
        // Scroll to the user message specifically (instant, no smooth scroll)
        userMessageElement.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else {
        // Fallback to scrolling the entire turn
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
      }

      // If streaming is active, pause auto-scroll and show scroll button
      // This allows the user to stay at the navigated position
      if (isStreaming && !autoScrollPaused) {
        setAutoScrollPaused(true);
        setShowScrollButton(true);
      }

      // Set interaction focus - this will lock the highlight for 2.5 seconds
      // The setInteractionFocus method is attached to the Set by useActiveMessage
      if (
        'setInteractionFocus' in activeMessageId &&
        typeof activeMessageId.setInteractionFocus === 'function'
      ) {
        (
          activeMessageId as Set<TurnId> & { setInteractionFocus: (id: TurnId) => void }
        ).setInteractionFocus(messageId);
      }
    },
    [activeMessageId, isStreaming, autoScrollPaused]
  );

  // Extract user messages for navigation - all turns have user messages
  const userMessages = useMemo(() => {
    return turns.map(turn => ({
      id: turn.id,
      content: turn.content.text,
      elementRef: userMessageRefsMap.get(turn.id),
    }));
  }, [turns, userMessageRefsMap]);

  // Calculate which messages are hidden by search filter
  const hiddenMessageIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set<TurnId>();
    const filteredIds = new Set(filteredTurns.map(t => t.id));
    return new Set(turns.filter(t => !filteredIds.has(t.id)).map(t => t.id));
  }, [turns, filteredTurns, searchQuery]);

  // ============================================================================
  // 渲染逻辑
  // ============================================================================
  const renderContent = () => {
    switch (state.type) {
      case 'loading':
        return (
          <div className='flex items-center justify-center h-full'>
            <div className='flex flex-col items-center space-y-4 animate-slide-up'>
              <div className='relative'>
                <div className='w-14 h-14 border-4 border-ui-muted/20 rounded-full'></div>
                <div className='absolute top-0 left-0 w-14 h-14 border-4 border-ui-primary border-t-transparent rounded-full animate-spin'></div>
              </div>
              <p className='text-ui-muted text-sm font-body'>加载中...</p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className='flex items-center justify-center h-full'>
            <div className='text-center px-6 max-w-md animate-slide-up'>
              <div className='w-16 h-16 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4 border border-red-100'>
                <span className='text-3xl'>⚠️</span>
              </div>
              <h3 className='heading-lg text-ui-charcoal mb-2 font-display'>出错了</h3>
              <p className='body-md text-ui-secondary font-body'>{state.message}</p>
            </div>
          </div>
        );

      case 'ready':
        if (!conversation) return null;

        return (
          <>
            {/* Turns */}
            <div className='max-w-4xl mx-auto px-6 py-6 space-y-6'>
              {filteredTurns.length === 0 ? (
                <div className='flex items-center justify-center h-full text-center'>
                  <div className='max-w-md flex flex-col items-center justify-center px-6 animate-slide-up'>
                    <div className='w-16 h-16 bg-ui-light rounded-xl flex items-center justify-center mb-4 shadow-ui'>
                      <span className='text-3xl'>💬</span>
                    </div>
                    <h3 className='heading-lg text-ui-charcoal mb-2 text-center font-display'>
                      {t('chat.startNewConversation')}
                    </h3>
                    <p className='body-md text-ui-secondary text-center font-body'>
                      {t('chat.noMessagesYet')}
                    </p>
                  </div>
                </div>
              ) : (
                filteredTurns.map((turn, index) => {
                  const isLastTurn = index === filteredTurns.length - 1;
                  const isStreamingTurn = turn.status === 'streaming';

                  return (
                    <div key={`${turn.id}-${turn.status}`} ref={getUserMessageRef(turn.id)}>
                      <TurnView
                        turn={turn}
                        streaming={isStreamingTurn}
                        onEdit={content => handleEditTurn(turn.id, content)}
                        onCopy={handleCopyTurn}
                        onLike={type => handleLikeTurn(turn.id, type)}
                        likeStatus={turnLikes[turn.id] || null}
                        showSuggestions={isLastTurn && !isStreaming}
                        suggestedActions={suggestedActions}
                        suggestedActionsLoading={
                          isLastTurn && !isStreaming ? suggestedActionsLoading : false
                        }
                        onSuggestedAction={handleSuggestedAction}
                        attachments={turn.content.attachments || []}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </>
        );
    }
  };

  return (
    <div className='flex flex-col h-screen bg-ui-cream font-body'>
      {/* Header */}
      <div className='flex-shrink-0 bg-ui-cream px-6 py-4 border-b border-ui-border'>
        <div className='flex items-center justify-between'>
          {/* Left side */}
          <div className='flex items-center gap-3 flex-1 min-w-0'>
            <button
              onClick={() => navigate('/')}
              className='lg:hidden p-2 hover:bg-ui-light rounded-ui-md flex-shrink-0 transition-colors'
            >
              <ArrowLeft size={18} className='text-ui-charcoal' />
            </button>

            <div className='flex items-center gap-3 min-w-0 flex-1'>
              <h1 className='heading-lg text-ui-charcoal truncate font-display'>
                {state.type === 'ready' && conversation
                  ? conversation.title || t('chat.newConversation')
                  : t('chat.newConversation')}
              </h1>
              {state.type === 'ready' &&
                conversation &&
                (() => {
                  const latestModelRef = getLatestModelRef();
                  return latestModelRef ? (
                    <span className='px-3 py-1 text-xs font-semibold text-white bg-ui-primary rounded-full flex-shrink-0 font-display'>
                      {getModelDisplayName(latestModelRef)}
                    </span>
                  ) : null;
                })()}
            </div>
          </div>

          {/* Right side */}
          <div className='flex items-center space-x-3 flex-shrink-0'>
            {/* New conversation button - mobile only */}
            <button
              onClick={() => navigate('/chat')}
              className='lg:hidden p-2 hover:bg-ui-light rounded-ui-md flex-shrink-0 transition-colors'
              aria-label={t('chat.newConversation')}
              title={t('chat.newConversation')}
            >
              <MessageSquare size={18} className='text-ui-charcoal' />
            </button>

            <div className='relative hidden sm:block'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-ui-muted'
                size={16}
              />
              <input
                type='text'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('chat.searchMessages', '搜索对话')}
                className='pl-10 pr-4 py-2 border border-ui-border rounded-ui-md focus:outline-none focus:ring-2 focus:ring-ui-primary/50 focus:border-ui-primary w-64 text-sm bg-white transition-all duration-150 font-body placeholder:text-ui-muted'
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div
        ref={messagesContainerRef}
        className='flex-1 overflow-y-auto bg-ui-cream scrollbar-ui'
        style={{ minHeight: 0 }}
      >
        {renderContent()}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed at bottom */}
      {state.type === 'ready' && conversation && (
        <div className='flex-shrink-0 w-full bg-ui-cream border-t border-ui-border'>
          <div className='max-w-4xl mx-auto px-6 py-4'>
            <EnhancedChatInput
              onSendMessage={data =>
                handleCreateTurn({
                  content: data.content,
                  model_id: data.model_id,
                  knowledge_base_ids: data.knowledge_base_ids,
                  files: data.files,
                  literature_sources: data.literature_sources,
                  literature_options: data.literature_options,
                })
              }
              disabled={isStreaming}
              selectedModel={selectedModel}
              models={models}
              onSelectModel={selectModel}
              kbState={kbState}
              onToggleKnowledgeBase={selectKnowledgebase}
              onSelectAllKnowledgeBases={selectAllKnowledgebases}
              onClearAllKnowledgeBases={clearAllKnowledgebases}
              literatureSources={literatureSources}
              onSelectLiterature={selectLiterature}
              literatureOptions={literatureOptions}
              onSelectLiteratureOptions={selectLiteratureOptions}
              placeholder={
                turns.length > 0
                  ? t('chat.continueConversation', '继续对话')
                  : t('chat.inputPlaceholder', 'Type your message here... 🤖')
              }
            />
            {/* AI Disclaimer */}
            <p className='text-xs text-ui-muted text-center mt-2 font-body'>
              {t('chat.aiDisclaimer', 'Answers are generated by AI and are for reference only.')}
            </p>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {chatError && (
        <div className='fixed bottom-6 right-6 bg-red-600 text-white px-4 py-3 rounded-ui-md shadow-ui-lg max-w-sm animate-slide-up z-50'>
          <p className='text-sm font-display font-medium'>{chatError}</p>
        </div>
      )}

      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <button
          onClick={() => {
            scrollToBottom(true);
            setAutoScrollPaused(false);
            setShowScrollButton(false);
          }}
          className='fixed bottom-24 right-6 bg-ui-primary hover:bg-ui-accent text-white p-3 rounded-full shadow-ui-md hover:shadow-float transition-all duration-200 z-50'
          title='Scroll to bottom'
          aria-label='Scroll to bottom of streaming content'
        >
          <ArrowDown size={20} />
        </button>
      )}

      {/* Message Navigator - Desktop only, 2+ messages */}
      {state.type === 'ready' && conversation && userMessages.length >= 2 && (
        <MessageNavigator
          userMessages={userMessages}
          visibleMessageIds={activeMessageId}
          hiddenMessageIds={hiddenMessageIds}
          onMessageClick={handleNavigatorMessageClick}
        />
      )}
    </div>
  );
};

export default ChatPage;
